// Import React lib
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Import style file
import {
    HomeWrapper,
    CardSection,
    ButtonSection,
    ProductLink,
    Icon,
    ButtonLink,
} from './style';

// Import Global styles
import { Loader } from '../../utils/styles/Atoms';

// Import contexts
import { AuthContext } from '../../utils/contexts/AuthContext';

// Import components
import { ErrorMessage } from '../../components/Error';
import ProductCard from '../../components/ProductCard';

// Import hooks
import { useFetchProducts } from '../../utils/hooks/products';
import { useFilterProductList } from '../../utils/hooks/filterProducts';

// Import assets
import { ReactComponent as addIcon } from '../../assets/icons/add.svg';

import imageNotFound from '../../assets/images/imageNotFound.png';

// Import texts
import errorTexts from '../../texts/error';

// Home page
function Home({ ICON_WIDTH = 30 }) {
    // Get the logged user
    const { isAdmin } = useContext(AuthContext);

    // Get URL GET params
    const [searchParams] = useSearchParams();
    const query = searchParams.get('query');

    // Get product list
    const {
        data: products,
        isLoading: isFetchProductsLoading,
        isFetching: isFetchProductsFetching,
        isError: isFetchProductError,
    } = useFetchProducts();

    // If a query is provided
    const {
        filteredProductList,
        isLoading: isFilteredProductListLoading,
        isError: isFilteredProductListError,
    } = useFilterProductList(products, query);

    // State to know if every image has been loaded or not
    const [areImagesLoaded, setAreImagesLoaded] = useState(false);
    const [loadedImagesCount, setLoadedImagesCount] = useState(0);
    const imagesToLoadCount =
        query && filteredProductList && filteredProductList.length > 0
            ? filteredProductList?.length
            : products?.length;

    // Callback function to add 1 to loaded images count
    function imageLoaded() {
        setLoadedImagesCount((prev) => prev + 1);
    }

    useEffect(() => {
        if (loadedImagesCount >= imagesToLoadCount) setAreImagesLoaded(true);
    }, [loadedImagesCount, imagesToLoadCount]);

    // As the error to display is the same for product list and filtered
    // combine error and loading states
    const isLoading =
        isFetchProductsLoading ||
        isFetchProductsFetching ||
        isFilteredProductListLoading;
    const isError = isFetchProductError || isFilteredProductListError;

    // Return the loader when the datas are loading
    if (isLoading) {
        return (
            <HomeWrapper>
                <Loader />
            </HomeWrapper>
        );
    }

    // Return an error if the datas failed to be loaded
    if (isError) {
        return (
            <HomeWrapper>
                {isAdmin && (
                    <ButtonSection>
                        <ButtonLink
                            to={{
                                pathname: '/product/new',
                            }}
                        >
                            <Icon as={addIcon} width={ICON_WIDTH * 1.5} />
                        </ButtonLink>
                    </ButtonSection>
                )}
                <ErrorMessage
                    critical={true}
                    message={errorTexts.productsLoadingError}
                />
            </HomeWrapper>
        );
    }

    // Return no product found error if no product were found in the db
    if (
        !products ||
        !products.length > 0 ||
        (query && (!filteredProductList || !filteredProductList.length > 0))
    ) {
        return (
            <HomeWrapper>
                {isAdmin && (
                    <ButtonSection>
                        <ButtonLink
                            to={{
                                pathname: '/product/new',
                            }}
                        >
                            <Icon as={addIcon} width={ICON_WIDTH * 1.5} />
                        </ButtonLink>
                    </ButtonSection>
                )}
                <ErrorMessage
                    critical={true}
                    message={errorTexts.noProductFound}
                />
            </HomeWrapper>
        );
    }

    return (
        <HomeWrapper>
            {!areImagesLoaded && <Loader />}
            {isAdmin && areImagesLoaded && (
                <ButtonSection>
                    <ButtonLink
                        to={{
                            pathname: '/product/new',
                        }}
                    >
                        <Icon as={addIcon} width={ICON_WIDTH * 1.5} />
                    </ButtonLink>
                </ButtonSection>
            )}
            <CardSection
                style={
                    !areImagesLoaded
                        ? {
                              display: 'none',
                          }
                        : undefined
                }
            >
                {query && filteredProductList && filteredProductList.length > 0
                    ? filteredProductList.map((product) => (
                          <ProductLink
                              key={product.id}
                              to={{
                                  pathname: `/product/${product.id}`,
                              }}
                          >
                              <ProductCard
                                  key={product.id}
                                  title={product.name}
                                  description={product.description}
                                  price={product.price}
                                  isOffer={product.is_offer}
                                  image={
                                      product.images &&
                                      product.images.length > 0 &&
                                      product.images[0].url
                                          ? product.images[0].url
                                          : imageNotFound
                                  }
                                  imageLoadedCallback={imageLoaded}
                              />
                          </ProductLink>
                      ))
                    : products.map((product) => (
                          <ProductLink
                              key={product.id}
                              to={{
                                  pathname: `/product/${product.id}`,
                              }}
                          >
                              <ProductCard
                                  key={product.id}
                                  title={product.name}
                                  description={product.description}
                                  price={product.price}
                                  isOffer={product.is_offer}
                                  image={
                                      product.images &&
                                      product.images.length > 0 &&
                                      product.images[0].url
                                          ? product.images[0].url
                                          : imageNotFound
                                  }
                                  imageLoadedCallback={imageLoaded}
                              />
                          </ProductLink>
                      ))}
            </CardSection>
        </HomeWrapper>
    );
}

export default Home;
